import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import type { Entitlements } from '@snap-platform/shared';
import { BrowserProvider } from 'ethers';
import { useAuth } from '../context/AuthContext.js';
import { useSnap } from '../hooks/useSnap.js';
import { SNAP_VERSION } from '../lib/snapConfig.js';
import { LATEST_SNAP_VERSION } from '../config/snapVersion.js';
import {
  ShieldCheckIcon,
  WalletIcon,
  CreditCardIcon,
  PlugIcon,
  BarChartIcon,
  ActivityIcon,
  SearchIcon,
  AlertTriangleIcon,
  CheckCircleIcon,
} from '../components/Icons.js';
import { safeCheckoutRedirect, resetCheckoutGuard } from '../services/checkout.js';
import { getRiskLabel } from '../lib/riskLabels.js';
import {
  createCheckoutSession,
  getEntitlements,
  connectWebWallet,
  getWebNonce,
  verifyWebSignature,
  getWebScanHistory,
  type WebScanRecord,
} from '../services/api.js';
import { ManageBillingButton } from '../components/ManageBillingButton.js';
import { InstallSnapButton } from '../components/InstallSnapButton.js';
import { trackConversion } from '../services/conversionTracking.js';
import { useUpgradePrompt } from '../hooks/useUpgradePrompt.js';
import { UpgradePromptModal } from '../components/UpgradePromptModal.js';

const PRO_SCAN_LIMIT = 75;
const FREE_SCAN_LIMIT = 5;

const WEB_TOKEN_KEY = 'web_auth_token';
const WEB_WALLET_KEY = 'web_wallet_address';

const CHAIN_LABELS: Record<string, string> = {
  eth: 'ETH',
  sol: 'SOL',
  base: 'BASE',
  bnb: 'BNB',
  avax: 'AVAX',
};

const RISK_COLORS: Record<string, string> = {
  GREEN: 'var(--accent-success, #22c55e)',
  YELLOW: 'var(--accent-warning, #eab308)',
  RED: 'var(--accent-danger, #ef4444)',
  UNKNOWN: 'var(--text-muted, #95a5a6)',
};

function truncateAddress(address: string): string {
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

export function Dashboard() {
  const { token, walletAddress, disconnect } = useAuth();
  const navigate = useNavigate();
  const snap = useSnap(token);

  const [entitlements, setEntitlements] = useState<Entitlements | null>(null);
  const [entitlementsLoading, setEntitlementsLoading] = useState(true);
  const upgradePrompt = useUpgradePrompt(entitlements);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Web auth for scan history
  const [webToken, setWebToken] = useState<string | null>(() => localStorage.getItem(WEB_TOKEN_KEY));
  const [scanHistory, setScanHistory] = useState<WebScanRecord[]>([]);
  const [scanHistoryLoading, setScanHistoryLoading] = useState(false);

  useEffect(() => { resetCheckoutGuard(); }, []);

  useEffect(() => {
    if (!token) {
      setEntitlementsLoading(false);
      return;
    }
    setEntitlementsLoading(true);
    getEntitlements(token)
      .then(setEntitlements)
      .catch((err) => {
        console.error('[Dashboard] Failed to load entitlements:', err);
      })
      .finally(() => setEntitlementsLoading(false));
  }, [token]);

  // Register wallet in web_users table
  useEffect(() => {
    if (walletAddress) {
      connectWebWallet(walletAddress).catch(() => {});
    }
  }, [walletAddress]);

  // Web auth: auto-authenticate for scan history JWT
  const authenticateWeb = useCallback(async () => {
    if (!walletAddress || !window.ethereum) return;
    try {
      const provider = new BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      const { nonce } = await getWebNonce(walletAddress);
      const signature = await signer.signMessage(nonce);
      const result = await verifyWebSignature(walletAddress, signature, nonce);
      localStorage.setItem(WEB_TOKEN_KEY, result.token);
      localStorage.setItem(WEB_WALLET_KEY, result.walletAddress);
      setWebToken(result.token);
    } catch {
      // Non-critical — scan history just won't load
    }
  }, [walletAddress]);

  useEffect(() => {
    if (walletAddress && !webToken) {
      authenticateWeb();
    }
  }, [walletAddress, webToken, authenticateWeb]);

  // Load scan history
  const loadScanHistory = useCallback(async (wt: string) => {
    setScanHistoryLoading(true);
    try {
      const { scans: data } = await getWebScanHistory(wt);
      setScanHistory(data);
    } catch {
      localStorage.removeItem(WEB_TOKEN_KEY);
      localStorage.removeItem(WEB_WALLET_KEY);
      setWebToken(null);
      setScanHistory([]);
    } finally {
      setScanHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (webToken) {
      loadScanHistory(webToken);
    }
  }, [webToken, loadScanHistory]);

  async function handleSubscribe() {
    if (!token) return;
    trackConversion('upgrade_clicked', { source: 'dashboard' });
    setCheckoutLoading(true);
    setError(null);
    trackConversion('checkout_started', { source: 'dashboard' });
    await safeCheckoutRedirect(
      () => createCheckoutSession(token),
      {
        onError: (err) => {
          setError(err.message || 'Failed to create checkout session');
          setCheckoutLoading(false);
        },
      },
    );
  }

  async function handleDisconnect() {
    await disconnect();
    navigate('/');
  }

  // Derived state
  const isPro = entitlements?.tier === 'pro';
  const snapInstalled = snap.isInstalled;
  const scansUsed = entitlements?.scansUsedThisMonth ?? 0;
  const scanLimit = entitlements?.monthlyLimit ?? (isPro ? PRO_SCAN_LIMIT : FREE_SCAN_LIMIT);
  const scansRemaining = entitlements?.scansRemaining ?? (scanLimit - scansUsed);
  const quotaPct = Math.min(100, (scansUsed / scanLimit) * 100);
  const recentScans = scanHistory.slice(0, 5);

  useEffect(() => { document.title = 'CryptoGuardians \u2014 Account Center'; }, []);

  if (entitlementsLoading && !entitlements) {
    return (
      <div className="dashboard">
        <div className="container" style={{ textAlign: 'center', padding: '4rem 0' }}>
          <span className="spinner" /> Loading Account Center...
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="container">

        {error && (
          <div className="rc-error-banner" style={{ marginBottom: '1rem' }}>
            <AlertTriangleIcon className="rc-error-banner-icon" aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        {/* ============================================================ */}
        {/* SECTION 1 — Account Identity Header                         */}
        {/* ============================================================ */}
        <div className="dash-card ac-identity-header">
          <div className="ac-identity-top">
            <div className="ac-identity-info">
              <div className="ac-identity-wallet">
                <WalletIcon className="ac-identity-wallet-icon" aria-hidden="true" />
                <span className="ac-identity-address">
                  {walletAddress ? truncateAddress(walletAddress) : 'Connected'}
                </span>
              </div>
              <span className={`ac-tier-badge ac-tier-${isPro ? 'pro' : 'free'}`}>
                {isPro ? 'PRO' : 'FREE'}
              </span>
            </div>
            <div className="ac-identity-actions">
              {isPro ? (
                <ManageBillingButton variant="secondary" />
              ) : entitlements ? (
                <button
                  className="btn btn-primary btn-primary-glow"
                  onClick={handleSubscribe}
                  disabled={checkoutLoading}
                >
                  {checkoutLoading ? 'Redirecting...' : 'Upgrade to PRO'}
                </button>
              ) : null}
            </div>
          </div>

          <div className="ac-usage-row">
            <div className="ac-usage-info">
              <BarChartIcon className="ac-usage-icon" aria-hidden="true" />
              <span className="ac-usage-text">
                Scans: <strong>{scansUsed}</strong> / {scanLimit} used
              </span>
            </div>
            <div className="ac-usage-bar-wrap">
              <div className="dash-progress-bar">
                <div
                  className={`dash-progress-fill ${quotaPct > 80 ? 'db-progress-warn' : ''}`}
                  style={{ width: `${quotaPct}%` }}
                />
              </div>
              <span className="ac-usage-remaining">{scansRemaining} remaining</span>
            </div>
          </div>
        </div>

        {/* ============================================================ */}
        {/* SECTION 2 — Security Center Access                          */}
        {/* ============================================================ */}
        <div className="dash-card ac-security-card">
          <div className="ac-security-content">
            <ShieldCheckIcon className="ac-security-icon" aria-hidden="true" />
            <div>
              <h2 className="ac-security-title">Security Dashboard</h2>
              <p className="ac-security-desc">
                Scan tokens, analyze contracts, and detect threats with our full security intelligence suite.
              </p>
            </div>
          </div>
          <button
            className="btn btn-primary btn-primary-glow ac-security-btn"
            onClick={() => navigate('/account/security')}
          >
            <SearchIcon className="ac-btn-icon" aria-hidden="true" />
            Launch Security Dashboard
          </button>
        </div>

        {/* ============================================================ */}
        {/* SECTION 3 — Snap Integration Module                         */}
        {/* ============================================================ */}
        <div className="dash-card ac-snap-card">
          <div className="dash-card-header">
            <PlugIcon className="dash-section-icon" aria-hidden="true" />
            <h2>MetaMask Snap Protection</h2>
          </div>
          {snap.isLoading ? (
            <p className="dash-card-subtitle">Detecting Snap status...</p>
          ) : snapInstalled ? (
            <div className="ac-snap-status ac-snap-installed">
              <CheckCircleIcon className="ac-snap-status-icon" aria-hidden="true" />
              <div>
                <span className="ac-snap-status-text">Snap Installed</span>
                {SNAP_VERSION && <span className="ac-snap-version">v{SNAP_VERSION}</span>}
              </div>
              {SNAP_VERSION && SNAP_VERSION < LATEST_SNAP_VERSION && (
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={snap.installSnap}
                  disabled={snap.isLoading}
                >
                  Update to v{LATEST_SNAP_VERSION}
                </button>
              )}
            </div>
          ) : (
            <div className="ac-snap-status ac-snap-not-installed">
              <p className="ac-snap-pitch">
                Install the CryptoGuardians Snap to get real-time transaction scanning directly inside MetaMask.
              </p>
              <InstallSnapButton variant="primary" />
              {snap.error && <p className="error-message" style={{ marginTop: '0.5rem' }}>{snap.error}</p>}
            </div>
          )}
        </div>

        {/* ============================================================ */}
        {/* SECTION 4 — Scan History Preview                            */}
        {/* ============================================================ */}
        <div className="dash-card">
          <div className="dash-card-header">
            <ActivityIcon className="dash-section-icon" aria-hidden="true" />
            <h2>Recent Scans</h2>
          </div>

          {scanHistoryLoading && (
            <p className="dash-card-subtitle">Loading scan history...</p>
          )}

          {!scanHistoryLoading && recentScans.length === 0 && (
            <div className="ac-empty-scans">
              <p>No scans yet</p>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => navigate('/account/security')}
              >
                Scan Your First Token
              </button>
            </div>
          )}

          {!scanHistoryLoading && recentScans.length > 0 && (
            <>
              <div className="ws-scan-table-wrap">
                <table className="ws-scan-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Chain</th>
                      <th>Token</th>
                      <th>Risk</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentScans.map((scan) => {
                      const r = scan.result as any;
                      const riskLevel = r?.riskLevel || 'Unknown';
                      const score = r?.score;
                      const tokenName = r?.project?.name || r?.project?.ticker || null;
                      const date = new Date(scan.created_at);

                      return (
                        <tr key={scan.id}>
                          <td className="ws-scan-date">
                            {date.toLocaleDateString()}
                          </td>
                          <td>
                            <span className="ws-chain-badge">{CHAIN_LABELS[scan.chain] || scan.chain.toUpperCase()}</span>
                          </td>
                          <td>
                            <Link to={`/search/${scan.chain}/${scan.token_address}`} className="ws-token-link">
                              {tokenName
                                ? tokenName
                                : `${scan.token_address.slice(0, 6)}...${scan.token_address.slice(-4)}`}
                            </Link>
                          </td>
                          <td>
                            <span
                              className={`ws-risk-badge ws-risk-${riskLevel}`}
                              style={RISK_COLORS[riskLevel] ? { backgroundColor: RISK_COLORS[riskLevel] } : undefined}
                            >
                              {getRiskLabel(riskLevel)}
                              {typeof score === 'number' ? ` ${score}` : ''}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {scanHistory.length > 5 && (
                <div className="ac-scan-history-more">
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => navigate('/account/security')}
                  >
                    View All Scans
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* ============================================================ */}
        {/* SECTION 5 — Upgrade Conversion Section                      */}
        {/* ============================================================ */}
        {!isPro && entitlements && (
          <div className="dash-card ac-upgrade-card">
            <div className="ac-upgrade-content">
              <CreditCardIcon className="ac-upgrade-icon" aria-hidden="true" />
              <div>
                <h2 className="ac-upgrade-title">Upgrade to PRO</h2>
                <ul className="ac-upgrade-features">
                  <li>Unlimited scans ({PRO_SCAN_LIMIT}/month)</li>
                  <li>Elite threat intelligence modules</li>
                  <li>Advanced exploit and sellability detection</li>
                  <li>Smart money and network analysis</li>
                </ul>
              </div>
            </div>
            <button
              className="btn btn-primary btn-primary-glow ac-upgrade-cta"
              onClick={() => navigate('/pricing')}
            >
              View Pricing
            </button>
          </div>
        )}

        {/* Compliance Disclaimer */}
        <div style={{ padding: '0.75rem 1rem', background: 'var(--surface-elevated, rgba(255,255,255,0.03))', borderRadius: '8px', marginTop: '1rem', fontSize: '0.75rem', color: 'var(--text-muted)', textAlign: 'center', lineHeight: 1.5 }}>
          CryptoGuardians provides blockchain intelligence for informational purposes only. Not financial advice.
        </div>

        {/* Quick links */}
        <div className="ac-quick-links">
          <Link to="/recover" className="ac-quick-link">Restore Subscription</Link>
          <Link to="/transfer" className="ac-quick-link">Transfer Subscription</Link>
          <Link to="/support" className="ac-quick-link">Support</Link>
        </div>

        {/* Disconnect */}
        <div className="dashboard-actions">
          <button className="btn btn-danger" onClick={handleDisconnect}>
            Disconnect Wallet
          </button>
        </div>

      </div>

      {upgradePrompt.shouldShow && upgradePrompt.hint && !isPro && (
        <UpgradePromptModal
          hint={upgradePrompt.hint}
          onUpgrade={() => {
            upgradePrompt.accept();
            if (upgradePrompt.hint?.type === 'version_update') {
              snap.installSnap();
            } else {
              handleSubscribe();
            }
          }}
          onDismiss={upgradePrompt.dismiss}
        />
      )}
    </div>
  );
}
